// src/lib/constants/player.ts

// Query keys (restructure to include cId at root level like other features)
export const PLAYER_QUERY_KEYS = {
  all: ['players'] as const,
  list: (cId: number) => [...PLAYER_QUERY_KEYS.all, 'list', cId] as const,
  detail: (cId: number, playerId: number) =>
    [...PLAYER_QUERY_KEYS.list(cId), playerId] as const,
} as const;

// Constraints (from legacy PlayerView.validate + InputValidator)
export const PLAYER_CONSTRAINTS = {
  LABEL_MIN: 1,
  LABEL_MAX: 100,
  DESC_SHORT_MAX: 250,
  PASSWORD_MIN: 1,
  PASSWORD_MAX: 100,
  OVERLAY_CLICK_URL_MIN: 10,
  OVERLAY_CLICK_URL_MAX: 300,
  PLAYLIST_BAR_LABEL_MIN: 1,
  PLAYLIST_BAR_LABEL_MAX: 12,
  AD_BREAK_MAX_DURATION_MIN: 1,
  AD_BREAK_MAX_DURATION_MAX: 300,
  VAST_URL_MIN: 10,
  SKIP_SECS_MIN: 1,
  SKIP_SECS_MAX: 60,
} as const;

// Video/Audio mode — wire values match PlayerVideoAudioMode enum
export const PLAYER_VIDEO_AUDIO_MODES = ['video', 'audio'] as const;
export type PlayerVideoAudioModeValue = typeof PLAYER_VIDEO_AUDIO_MODES[number];

export const PLAYER_VIDEO_AUDIO_MODE_LABELS: Record<PlayerVideoAudioModeValue, string> = {
  video: 'Video',
  audio: 'Audio',
} as const;

// Aspect ratios — wire values match AspectRatio enum
export const ASPECT_RATIOS = ['16:9', '4:3', 'custom'] as const;
export type AspectRatioValue = typeof ASPECT_RATIOS[number];

// Overlay positions — wire values match OverlayPosition enum
export const OVERLAY_POSITIONS = ['top_left', 'top_right', 'bottom_right', 'bottom_left'] as const;
export type OverlayPositionValue = typeof OVERLAY_POSITIONS[number];

export const OVERLAY_POSITION_LABELS: Record<OverlayPositionValue, string> = {
  top_left: 'Top Left',
  top_right: 'Top Right',
  bottom_right: 'Bottom Right',
  bottom_left: 'Bottom Left',
} as const;

// Ad source types — wire values match AdSourceType enum
export const AD_SOURCE_TYPES = ['none', 'vast', 'playlist'] as const;
export type AdSourceTypeValue = typeof AD_SOURCE_TYPES[number];

export const AD_SOURCE_TYPE_LABELS: Record<AdSourceTypeValue, string> = {
  none: 'None (Ads Disabled)',
  vast: 'VAST Tag URL',
  playlist: 'Ad Playlist',
} as const;

// Ad midroll inject types — wire values match AdMidrollInjectType enum
export const AD_MIDROLL_INJECT_TYPES = ['none', 'auto', 'cuepoint'] as const;
export type AdMidrollInjectTypeValue = typeof AD_MIDROLL_INJECT_TYPES[number];

export const AD_MIDROLL_INJECT_TYPE_LABELS: Record<AdMidrollInjectTypeValue, string> = {
  none: 'None',
  auto: 'Automatic',
  cuepoint: 'Cue Points',
} as const;

// ACL rule types — wire values match AclRuleType enum
export const ACL_RULE_TYPES = ['domain', 'geo_country', 'geo_region', 'geo_dma', 'geo_postalcode'] as const;
export type AclRuleTypeValue = typeof ACL_RULE_TYPES[number];

export const ACL_GEO_RULE_TYPES = ['geo_country', 'geo_region', 'geo_dma', 'geo_postalcode'] as const;
export type AclGeoRuleTypeValue = typeof ACL_GEO_RULE_TYPES[number];

export const ACL_GEO_RULE_LABELS: Record<AclGeoRuleTypeValue, { singular: string; plural: string }> = {
  geo_country: { singular: 'Country', plural: 'Countries' },
  geo_region: { singular: 'State/Region', plural: 'States/Regions' },
  geo_dma: { singular: 'DMA', plural: 'DMAs' },
  geo_postalcode: { singular: 'Zip Code', plural: 'Zip Codes' },
} as const;

// Geo filter types (derived from ACL isDeny flag)
export const GEO_FILTER_TYPES = ['any', 'allow', 'deny'] as const;
export type GeoFilterTypeValue = typeof GEO_FILTER_TYPES[number];

export const GEO_FILTER_TYPE_LABELS: Record<GeoFilterTypeValue, string> = {
  any: 'Any (No Restriction)',
  allow: 'Allow Only',
  deny: 'Deny',
} as const;

// Access control types (derived from security.password)
export const ACCESS_CONTROL_TYPES = ['none', 'password'] as const;
export type AccessControlTypeValue = typeof ACCESS_CONTROL_TYPES[number];

// Skip seconds options for Select dropdown
export const SKIP_SECONDS_OPTIONS = [5, 10, 15, 20, 30, 45, 60] as const;

// Default values for new players (from legacy PlayerView.create())
export const PLAYER_DEFAULTS = {
  AD_MIDROLL_AUTO_BREAK_INTERVAL: 720,
  AD_NOBREAK_INTERVAL: 720,
  AD_BREAK_MAX_DURATION: 60,
  VOLUME: 100,
  WIDTH: 768,
  HEIGHT: 432,
} as const;

// Grid constants
export const PLAYER_GRID = {
  ROW_HEIGHT: 56,
} as const;

// Sort options for the list page
export const PLAYER_SORT_OPTIONS = ['label', 'enabled', 'created', 'updated'] as const;
export type PlayerSortKey = typeof PLAYER_SORT_OPTIONS[number];

/** Default sort when no persisted preference exists: alphabetical by name */
export const DEFAULT_PLAYER_SORT_KEY: PlayerSortKey = 'label';
export const DEFAULT_PLAYER_SORT_DIRECTION = 'asc' as const;

export const PLAYER_SORT_LABELS: Record<PlayerSortKey, string> = {
  label: 'Name',
  enabled: 'Status',
  created: 'Date Created',
  updated: 'Date Updated',
} as const;
